import { Menu, menu } from "../../config/menu"
import React, { useEffect, useState } from "react"

import { A } from "../../utils/router"
import ConditionalWrapper from "../miscellaneous/conditional-wrapper"
import FocusLock from "react-focus-lock"
import HeaderMenuStyles from "./header-v2.module.css"
import { LazyImage } from "../universal/image"
import classNames from "classnames"
import { findTranslation } from "../../utils/find-translation"
import { useAuthState } from "../../context/auth"
import { useCCMLoaded } from "../../hooks/use-ccm-loaded"
import { useLocalesState } from "../../context/locales"
import { useTransitionState } from "react-transition-state"
import { useTranslation } from "react-i18next"

type SidebarProps = {
    contact?: boolean
    light?: boolean
    reset: () => void
    active: boolean
}

type SidebarMenu = {
    image_path: string
    image_alt?: string
    teaser_url?: string
    top_links: {
        title: string
        url: string
        id: string
    }[]
}

const sidebarMenu: SidebarMenu = {
    image_path: "/menu/teaser-startpaket.jpg",
    image_alt: "Mehr Infos zum LaVita Startpaket",
    teaser_url: "https://www.lavita.de/startpaket",
    top_links: [
        {
            title: "LaVita Abo mit Preisvorteil",
            url: "https://www.lavita.de/abo",
            id: "abo",
        },
        {
            title: "LaVita App",
            url: "https://www.lavita.de/service/app",
            id: "app",
        },
        {
            title: "Karriere bei LaVita",
            url: "https://www.lavita.de/karriere",
            id: "karriere",
        },
    ],
}

const Sidebar = ({ contact, light, reset, active }: SidebarProps) => {
    const { t } = useTranslation("shared")
    const { region, language } = useLocalesState()
    const { isPartner } = useAuthState()

    if (!["DE", "AT"].includes(region) || language !== "de") return null

    return (
        <div
            className={classNames(HeaderMenuStyles.sidebar, { [HeaderMenuStyles.sidebarLight]: light })}
            aria-hidden={!active}
        >
            <p>{t("header_menu_top_links")}</p>
            <ul>
                {sidebarMenu.top_links.map(l => {
                    return (
                        <li key={l.id}>
                            <A href={l.url} onClick={reset}>
                                {l.title}
                            </A>
                        </li>
                    )
                })}
            </ul>
            {sidebarMenu.image_path && (
                <div className={HeaderMenuStyles.imageWrapper}>
                    <ConditionalWrapper
                        condition={!!sidebarMenu.teaser_url}
                        wrapper={[
                            children => (
                                <A href={sidebarMenu.teaser_url} className={HeaderMenuStyles.image} onClick={reset}>
                                    {children}
                                </A>
                            ),
                            children => <div className={HeaderMenuStyles.image}>{children}</div>,
                        ]}
                    >
                        <LazyImage
                            url="assets"
                            path={sidebarMenu.image_path}
                            srcSet={[{ w: 342, default: true }, { w: 684 }]}
                            sizes="342px"
                            alt={sidebarMenu.image_alt}
                        />
                    </ConditionalWrapper>
                </div>
            )}
            {contact && (
                <div>
                    <div className={HeaderMenuStyles.contact}>
                        <i className="lavita-icon"></i>
                        <div>
                            <strong>{t("header_menu_phone")}</strong>
                            <a href={t(!isPartner ? "header_menu_phone_url" : "header_menu_phone_partner_url")}>
                                {t(!isPartner ? "header_menu_phone_number" : "header_menu_phone_partner_number")}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

type SubMenuItemProps = {
    item: Menu
    reset: () => void
}

const SubMenuItem = ({ item, reset }: SubMenuItemProps) => {
    const { language, region } = useLocalesState()

    if (!findTranslation(item, language)?.title || findTranslation(item, language)?.hiddenRegion?.includes(region))
        return null

    if (findTranslation(item, language)?.url) {
        return (
            <li className={HeaderMenuStyles.option}>
                <A href={findTranslation(item, language).url} onClick={reset}>
                    {findTranslation(item, language).title}
                </A>
            </li>
        )
    }

    if (item.options) {
        return (
            <li>
                <strong className={HeaderMenuStyles.heading}>{findTranslation(item, language)?.title}</strong>
                <ul className={HeaderMenuStyles.options}>
                    {item.options.map(o => (
                        <SubMenuItem item={o} key={o.id} reset={reset} />
                    ))}
                </ul>
            </li>
        )
    }

    return null
}

type MenuItemProps = {
    item: Menu
    setActive: React.Dispatch<React.SetStateAction<string | undefined>>
    active: boolean
    reset: () => void
}

const MenuItem = ({ item, setActive, active, reset }: MenuItemProps) => {
    const { t } = useTranslation("shared")
    const { language, region } = useLocalesState()

    const [{ status, isMounted, isResolved }, toggle] = useTransitionState({
        timeout: 250,
        mountOnEnter: true,
        unmountOnExit: true,
        preEnter: true,
    })

    useEffect(() => {
        toggle(active)
    }, [toggle, active])

    if (!findTranslation(item, language)?.title || findTranslation(item, language)?.hiddenRegion?.includes(region))
        return null

    if (findTranslation(item, language)?.url) {
        return (
            <li className={HeaderMenuStyles.mainHeading}>
                <A href={findTranslation(item, language).url} onClick={reset}>
                    {findTranslation(item, language).title}
                </A>
            </li>
        )
    }

    const itemUrl = findTranslation(item, language)?.url || item.url
    const isMagazin = item.id === "magazin"

    if (isMagazin) {
        const magazinUrl = language === "cs" ? "https://blogcz.lavita.com" : itemUrl
        return (
            <li className={classNames(HeaderMenuStyles.mainHeading, { [HeaderMenuStyles.magazinItem]: isMagazin })}>
                <A
                    href={magazinUrl}
                    onClick={reset}
                    className={classNames({ [HeaderMenuStyles.magazinLink]: isMagazin })}
                >
                    {findTranslation(item, language).title}
                </A>
            </li>
        )
    }

    if (item.options) {
        return (
            <li className={classNames(HeaderMenuStyles.mainHeading, { [HeaderMenuStyles.active]: active })}>
                <button
                    type="button"
                    onClick={() => setActive(active => (active === item.id ? undefined : item.id))}
                    id={`header-menu-${item.id}-trigger`}
                    aria-expanded={active}
                    aria-controls={`header-menu-${item.id}-content`}
                >
                    {findTranslation(item, language)?.title}
                </button>
                {isMounted && (
                    <div
                        className={classNames(HeaderMenuStyles.optionsWrapper, {
                            [HeaderMenuStyles.entering]: status === "entering" || status === "entered",
                        })}
                        id={`header-menu-${item.id}-content`}
                        aria-hidden={!active}
                        aria-labelledby={`header-menu-${item.id}-trigger`}
                    >
                        <FocusLock disabled={!isResolved} returnFocus>
                            <div className={HeaderMenuStyles.optionsHeader}>
                                <button
                                    type="button"
                                    onClick={() => setActive(undefined)}
                                    className={HeaderMenuStyles.back}
                                    tabIndex={!active ? -1 : undefined}
                                >
                                    <i className="lavita-icon"></i>
                                    <span>{t("back")}</span>
                                </button>
                            </div>
                            <div className={HeaderMenuStyles.optionsMain}>
                                <ul className={HeaderMenuStyles.options}>
                                    {item.options.map(o => (
                                        <SubMenuItem item={o} key={o.id} reset={reset} />
                                    ))}
                                </ul>
                                <Sidebar light reset={reset} active={active} />
                            </div>
                        </FocusLock>
                    </div>
                )}
            </li>
        )
    }

    return null
}

const HeaderMenu = () => {
    const { t } = useTranslation("shared")

    const { region } = useLocalesState()

    const [opened, setOpened] = useState(false)
    const [active, setActive] = useState<string>()

    const reset = () => {
        setOpened(false)
        setActive(undefined)
        document.body.classList.remove("noScroll")
    }

    const ccmLoaded = useCCMLoaded()

    return (
        <FocusLock
            disabled={!opened}
            returnFocus={suggestedNode => {
                if (suggestedNode.id === "header-menu-trigger") return true

                return false
            }}
        >
            <button
                className={classNames(HeaderMenuStyles.button, { [HeaderMenuStyles.light]: opened })}
                onClick={() =>
                    setOpened(open => {
                        if (open) {
                            setActive(undefined)
                            document.body.classList.remove("noScroll")
                        } else {
                            document.body.classList.add("noScroll")
                        }
                        return !open
                    })
                }
                id="header-menu-trigger"
                aria-expanded={opened}
                aria-controls="header-menu-content"
            >
                <div className={classNames(HeaderMenuStyles.hamburger, { [HeaderMenuStyles.close]: opened })}>
                    <div className={HeaderMenuStyles.line} />
                    <div className={HeaderMenuStyles.line} />
                </div>
                <span>{t(opened ? "header_menu_close" : "header_menu_open")}</span>
            </button>
            <div
                className={classNames(HeaderMenuStyles.menu, { [HeaderMenuStyles.opened]: opened })}
                id="header-menu-content"
                aria-hidden={!opened}
                aria-labelledby="header-menu-trigger"
            >
                <div className={HeaderMenuStyles.scroll}>
                    <div className={HeaderMenuStyles.container}>
                        <div className={HeaderMenuStyles.main}>
                            <nav
                                className={classNames(HeaderMenuStyles.navigation, {
                                    [HeaderMenuStyles.activeNavigation]: active,
                                })}
                                aria-label={t("header_menu")}
                            >
                                <ul>
                                    {menu.map(m => (
                                        <MenuItem
                                            item={m}
                                            key={m.id}
                                            setActive={setActive}
                                            reset={reset}
                                            active={active === m.id}
                                        />
                                    ))}
                                </ul>
                            </nav>
                            <Sidebar contact reset={reset} active={!active} />
                        </div>
                        <div
                            className={classNames(HeaderMenuStyles.footer, {
                                [HeaderMenuStyles.hidden]: active,
                            })}
                        >
                            {region !== "TR" && (
                                <A href={`${process.env.WEB_URL}/${t("footer_legalnotice_path")}`} onClick={reset}>
                                    {t("footer_legalnotice")}
                                </A>
                            )}
                            {region === "TR" && (
                                <A href={`${process.env.WEB_URL}/${t("footer_cookie_path")}`}>{t("footer_cookie")}</A>
                            )}
                            <A href={`${process.env.WEB_URL}/${t("footer_dataprotection_path")}`} onClick={reset}>
                                {t("footer_dataprotection")}
                            </A>
                            {region !== "TR" && (
                                <>
                                    <A href={`${process.env.WEB_URL}/${t("footer_terms_and_conditions_path")}`}>
                                        {t("footer_terms_and_conditions")}
                                    </A>
                                    <A href={`${process.env.WEB_URL}/${t("footer_revocation_path")}`}>
                                        {t("footer_revocation")}
                                    </A>
                                </>
                            )}
                            {region !== "TR" && ccmLoaded && (
                                <button onClick={() => window.CCM?.openControlPanel()}>{t("footer_cookie")}</button>
                            )}
                            {region === "TR" && (
                                <A href={`${process.env.WEB_URL}/${t("footer_distance_contract_path")}`}>
                                    {t("footer_distance_contract")}
                                </A>
                            )}
                            <A href={`${process.env.WEB_URL}/${t("footer_contact_path")}`} onClick={reset}>
                                {t("footer_contact")}
                            </A>
                            {region === "DE" && <A href={t("footer_whistleblower_url")}>{t("footer_whistleblower")}</A>}
                            {region !== "CH" && region !== "TR" && region !== "CZ" && (
                                <A href={`${process.env.WEB_URL}/${t("footer_accessibility_path")}`}>
                                    {t("footer_accessibility")}
                                </A>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </FocusLock>
    )
}

export default React.memo(HeaderMenu)
